/**
 * useElectionMapData Composable
 *
 * Fetches election map sentiment data from the /election/map API
 * for map visualization, and /election/grid API for the per-product
 * aggregate sentiment bar and confidence bands.
 */
import type { MaybeRefOrGetter, ComputedRef } from 'vue';
import { computed, toValue } from 'vue';
import { media } from '@ibgroup-tws-webapps/ratio.ui';
import { useGetElectionMap, useGetElectionGrid } from '@/api/forecasttrader.queries';
import { stateNameToCode } from '../utils/stateMapping';
import { getPartyColor } from '../constants/partyColors';
import type { USStateCode } from '../types/election.types';
import { STATE_TO_FIPS } from '../types/election.types';
import type {
  ForecastTraderElectionMapProduct,
  ForecastTraderElectionMapParams,
  ForecastTraderElectionGridParams,
} from '@/types/forecasttrader.types';

/**
 * Transformed state data for map rendering
 */
export interface StateColorData {
  readonly stateCode: USStateCode;
  readonly fips: string;
  readonly color: string;
  readonly party: string;
  readonly intensity: number;
  readonly products: ReadonlyArray<ForecastTraderElectionMapProduct>;
}

/**
 * Party aggregates for sentiment bar
 */
export interface PartyAggregates {
  readonly gop: number;
  readonly dem: number;
  readonly ind: number;
  readonly total: number;
}

/**
 * A single confidence-level band in the progress bar
 */
export interface ConfidenceBand {
  readonly party: 'Democrat' | 'Republican' | 'Other';
  /** 0=toss-up, 1=lean, 2=likely, 3=solid */
  readonly intensity: number;
  /** Number of races/seats at this confidence level */
  readonly count: number;
}

/** Total seats per office for progress bar denominators */
export const TOTAL_SEATS: Record<string, number> = {
  governor: 50,
  senate: 100,
  house: 435,
};

/**
 * Return type for useElectionMapData composable
 */
export interface UseElectionMapDataReturn {
  /** Map of FIPS code -> color string for SVG fill */
  readonly stateFills: ComputedRef<Map<string, string>>;
  /** Map of state code -> full state data */
  readonly stateData: ComputedRef<Map<USStateCode, StateColorData>>;
  /** Aggregated party counts for sentiment bar */
  readonly partyAggregates: ComputedRef<PartyAggregates>;
  /** Confidence-level bands for the enhanced progress bar */
  readonly confidenceBands: ComputedRef<ConfidenceBand[]>;
  /** Aggregated open interest for all products (by office) */
  readonly openInterest: ComputedRef<number>;
  /** Loading state */
  readonly isLoading: ComputedRef<boolean>;
  /** Error state */
  readonly error: ComputedRef<Error | null>;
}

/**
 * Composable for fetching and transforming election map data
 *
 * @param params - Election map params (office, raceType, party)
 *
 * @example
 * ```vue
 * <script setup>
 * const params = computed(() => ({ office: 'governor', raceType: 'general' }))
 * const { stateFills, partyAggregates, isLoading } = useElectionMapData(params)
 * </script>
 * ```
 */
export function useElectionMapData(
  params: MaybeRefOrGetter<ForecastTraderElectionMapParams | null>,
): UseElectionMapDataReturn {
  // Fetch election map data from API (for SVG map coloring)
  const { data: mapData, isLoading: mapLoading, error: mapError } = useGetElectionMap(computed(() => toValue(params)));

  // Build grid params from map params (same office/raceType/party, no filters)
  const gridParams = computed<ForecastTraderElectionGridParams | null>(() => {
    const p = toValue(params);
    if (!p) return null;
    return {
      office: p.office,
      raceType: p.raceType,
      party: p.raceType === 'primary' ? (p as { party: 'Democrat' | 'Republican' }).party : undefined,
    };
  });

  // Fetch election grid data from API (for per-product progress bar)
  const { data: gridData, isLoading: gridLoading, error: gridError } = useGetElectionGrid(gridParams);

  // Transform API response to state data map (re-evaluates on theme change)
  const stateData = computed<Map<USStateCode, StateColorData>>(() => {
    const result = new Map<USStateCode, StateColorData>();
    if (!mapData.value?.states) return result;

    const isDark = media.scheme === 'dark';

    for (const state of mapData.value.states) {
      const stateCode = stateNameToCode(state.name);
      if (!stateCode) {
        console.warn(`[useElectionMapData] Unknown state name: ${state.name}`);
        continue;
      }

      const fips = STATE_TO_FIPS[stateCode];
      if (!fips) continue;

      result.set(stateCode, {
        stateCode,
        fips,
        color: getPartyColor(state.party ?? '', state.color ?? 0, isDark),
        party: state.party ?? '',
        intensity: state.color ?? 0,
        products: state.products,
      });
    }

    return result;
  });

  // Pre-compute fills for SVG rendering (FIPS -> color)
  const stateFills = computed<Map<string, string>>(() => {
    const fills = new Map<string, string>();

    for (const [, stateInfo] of stateData.value) {
      fills.set(stateInfo.fips, stateInfo.color);
    }

    return fills;
  });

  // Compute party aggregates from grid data (per-product party/color)
  // Products with no party or no color are counted as toss-ups (ind)
  const partyAggregates = computed<PartyAggregates>(() => {
    const products = gridData.value?.products;
    if (!products) return { gop: 0, dem: 0, ind: 0, total: 0 };

    let gop = 0,
      dem = 0,
      ind = 0;

    for (const product of products) {
      if (!product.party || product.color == null) ind++;
      else if (product.party === 'Republican') gop++;
      else if (product.party === 'Democrat') dem++;
      else ind++;
    }

    return { gop, dem, ind, total: gop + dem + ind };
  });

  // Compute confidence bands from grid data (per-product party/color)
  const confidenceBands = computed<ConfidenceBand[]>(() => {
    const products = gridData.value?.products;
    if (!products) return [];

    // Bucket: key = "party:intensity", value = count of products
    const buckets = new Map<string, number>();

    for (const product of products) {
      let party: ConfidenceBand['party'];
      let intensity: number;

      if (!product.party || product.color == null) {
        party = 'Other';
        intensity = 0;
      } else if (product.party === 'Republican') {
        party = 'Republican';
        intensity = product.color;
      } else if (product.party === 'Democrat') {
        party = 'Democrat';
        intensity = product.color;
      } else {
        party = 'Other';
        intensity = 0;
      }

      const key = `${party}:${intensity}`;
      buckets.set(key, (buckets.get(key) ?? 0) + 1);
    }

    const bands: ConfidenceBand[] = [];
    for (const [key, count] of buckets) {
      const [party, intensityStr] = key.split(':');
      bands.push({
        party: party as ConfidenceBand['party'],
        intensity: Number(intensityStr),
        count,
      });
    }

    // Sort in visual order: DEM solid(3)→toss-up(0), then Other, then REP toss-up(0)→solid(3)
    const partyOrder: Record<string, number> = { Democrat: 0, Other: 1, Republican: 2 };
    bands.sort((a, b) => {
      const pa = partyOrder[a.party] ?? 1;
      const pb = partyOrder[b.party] ?? 1;
      if (pa !== pb) return pa - pb;
      // DEM: highest intensity first (solid→toss-up); REP: lowest intensity first (toss-up→solid)
      if (a.party === 'Democrat') return b.intensity - a.intensity;
      return a.intensity - b.intensity;
    });

    return bands;
  });

  const openInterest = computed(() => {
    const raw = mapData.value?.open_interest;
    if (raw == null || typeof raw !== 'number' || isNaN(raw)) return 0;
    return raw;
  });

  return {
    stateFills,
    stateData,
    partyAggregates,
    confidenceBands,
    openInterest,
    isLoading: computed(() => mapLoading.value || gridLoading.value),
    error: computed(() => mapError.value || gridError.value),
  };
}
